import { useCallback, useMemo, useState, useEffect } from 'react'
import { secureJsonFetch } from '@/utils/SecureFetch'
import { usePaginatedQuery } from './UsePaginatedQuery'
import { fetchPlaybookPlaysPage, playKeys } from '@/api/queries/PlayQueries'
import {
	fetchPlaybookTrash,
	trashBlock,
	restoreBlock,
	permanentDeleteBlock,
} from '@/api/queries/BlockQueries'
import type { Drawing } from '@/types/Drawing.types'
import type { Player } from '@/types/Player.types'

export interface Play {
	id: string
	name: string
	formation: string
	personnel?: string
	playType: string
	defensiveFormation: string
	labels: string[]
	labelObjects?: { id: string; name: string; color: string }[]
	lastModified: string
	drawings?: Drawing[]
	players?: Player[]
}

interface UsePlaybookDataReturn {
	plays: Play[]
	trashedPlays: Play[]
	isLoading: boolean
	error: string | null
	createPlay: (name: string) => Promise<Play>
	updatePlay: (playId: string, updates: Partial<Play>) => Promise<void>
	deletePlay: (playId: string) => Promise<void>
	restorePlay: (playId: string) => Promise<void>
	permanentDeletePlay: (playId: string) => Promise<void>
	duplicatePlay: (playId: string) => Promise<Play>
	refetch: () => Promise<void>
	refetchTrash: () => Promise<void>
	fetchNextPlays: () => Promise<void>
	hasMorePlays: boolean
	isFetchingPlays: boolean
}

export function usePlaybookData(playbookId: string | undefined): UsePlaybookDataReturn {
	const [trashedPlays, setTrashedPlays] = useState<Play[]>([])

	// Use paginated query for plays
	const {
		items: plays,
		isLoading: playsLoading,
		error: playsError,
		fetchNextPage: fetchNextPlays,
		hasNextPage: hasMorePlays,
		isFetchingNextPage: isFetchingPlays,
		removeItem: removePlay,
		invalidate: invalidatePlays,
	} = usePaginatedQuery({
		queryKey: playbookId ? playKeys.list(playbookId) : ['plays-empty'],
		fetchPage: async (cursor) => {
			if (!playbookId) throw new Error('No playbook ID')
			const result = await fetchPlaybookPlaysPage(playbookId, 20, cursor)
			return {
				items: result.items ?? [],
				nextCursor: result.nextCursor ?? undefined,
				hasMore: result.nextCursor != null,
			}
		},
		getItemId: (play) => play.id,
		enabled: !!playbookId,
	})

	// Transform API play to component format
	const transformPlay = useCallback((apiPlay: any): Play => {
		return {
			id: apiPlay.id,
			name: apiPlay.name || '',
			formation: apiPlay.formation_id ?? '',
			personnel: apiPlay.personnel_id ?? undefined,
			playType: apiPlay.play_type || '',
			defensiveFormation: apiPlay.defensive_formation_id ?? '',
			labels: (apiPlay.labels || []).map((l: any) => typeof l === 'string' ? l : l.name),
			labelObjects: apiPlay.labels || [],
			lastModified: apiPlay.updated_at ? new Date(apiPlay.updated_at).toLocaleDateString() : new Date().toLocaleDateString(),
			drawings: apiPlay.drawings || [],
			players: apiPlay.players || [],
		}
	}, [])

	// Transform plays via useMemo
	const transformedPlays = useMemo(
		() => plays.map(transformPlay),
		[plays, transformPlay],
	)

	// Fetch trashed plays
	useEffect(() => {
		if (!playbookId) return

		const fetchTrash = async () => {
			try {
				const result = await fetchPlaybookTrash(playbookId)
				const transformed = (result.items || []).map(transformPlay)
				setTrashedPlays(transformed)
			} catch (err) {
				console.error('Failed to fetch trashed plays:', err)
			}
		}

		fetchTrash()
	}, [playbookId, transformPlay])

	// Refetch — just invalidate plays cache
	const refetch = useCallback(async () => {
		invalidatePlays()
	}, [invalidatePlays])

	// Refetch trashed plays
	const refetchTrash = useCallback(async () => {
		if (!playbookId) return

		try {
			const result = await fetchPlaybookTrash(playbookId)
			const transformed = (result.items || []).map(transformPlay)
			setTrashedPlays(transformed)
		} catch (err) {
			console.error('Failed to fetch trashed plays:', err)
		}
	}, [playbookId, transformPlay])

	// Create play
	const createPlay = useCallback(async (_name: string): Promise<Play> => {
		if (!playbookId) throw new Error('No playbook ID')

		const response = await secureJsonFetch(`/api/playbooks/${playbookId}/plays`, {
			method: 'POST',
			body: { name: _name },
		})

		if (response.status === 403) {
			throw new Error('You do not have permission to create plays in this playbook')
		}
		if (response.status === 404) {
			throw new Error('Playbook not found')
		}
		if (!response.ok) {
			const data = await response.json()
			throw new Error(data.error || 'Failed to create play')
		}

		const data = await response.json()
		invalidatePlays()
		return transformPlay(data.play)
	}, [playbookId, invalidatePlays, transformPlay])

	// Update play
	const updatePlay = useCallback(async (playId: string, updates: Partial<Play>): Promise<void> => {
		const response = await secureJsonFetch(`/api/plays/${playId}`, {
			method: 'PUT',
			body: updates,
		})

		if (response.status === 403) {
			throw new Error('You do not have permission to edit this play')
		}
		if (response.status === 404) {
			invalidatePlays()
			throw new Error('Play not found')
		}
		if (!response.ok) {
			const data = await response.json()
			throw new Error(data.error || 'Failed to update play')
		}

		invalidatePlays()
	}, [invalidatePlays])

	// Delete play (soft delete - moves to trash)
	const deletePlay = useCallback(async (playId: string): Promise<void> => {
		removePlay(playId)

		try {
			await trashBlock(playId)
			invalidatePlays()
			await refetchTrash()
		} catch (err) {
			invalidatePlays()
			throw err
		}
	}, [removePlay, invalidatePlays, refetchTrash])

	// Restore play from trash
	const restorePlay = useCallback(async (playId: string): Promise<void> => {
		setTrashedPlays(prev => prev.filter(p => p.id !== playId))

		try {
			await restoreBlock(playId)
			invalidatePlays()
			await refetchTrash()
		} catch (err) {
			await refetchTrash()
			throw err
		}
	}, [invalidatePlays, refetchTrash])

	// Permanently delete play
	const permanentDeletePlay = useCallback(async (playId: string): Promise<void> => {
		setTrashedPlays(prev => prev.filter(p => p.id !== playId))

		try {
			await permanentDeleteBlock(playId)
			await refetchTrash()
		} catch (err) {
			await refetchTrash()
			throw err
		}
	}, [refetchTrash])

	// Duplicate play
	const duplicatePlay = useCallback(async (playId: string): Promise<Play> => {
		const response = await secureJsonFetch(`/api/plays/${playId}/duplicate`, {
			method: 'POST',
		})

		if (response.status === 403) {
			throw new Error('You do not have permission to duplicate this play')
		}
		if (response.status === 404) {
			invalidatePlays()
			throw new Error('Play not found')
		}
		if (!response.ok) {
			const data = await response.json()
			throw new Error(data.error || 'Failed to duplicate play')
		}

		const data = await response.json()
		invalidatePlays()
		return transformPlay(data.play)
	}, [invalidatePlays, transformPlay])

	return {
		plays: transformedPlays,
		trashedPlays,
		isLoading: playsLoading,
		error: playsError ? playsError.message : null,
		createPlay,
		updatePlay,
		deletePlay,
		restorePlay,
		permanentDeletePlay,
		duplicatePlay,
		refetch,
		refetchTrash,
		fetchNextPlays,
		hasMorePlays,
		isFetchingPlays,
	}
}
